import { mfLogoBaseUrl } from '../../env'
import {
  PowerPerformanceForms,
  ToggleLabel,
  FinancialMetricTypes,
  RecommendedFund
} from '../../types/mutual-fund-detail.types'
import { convertNumberToCurrency, toPascalCaseWithSpaces } from '../../utils/utils'
import { PowerFormBadge } from '../ui-components/PowerFormBadge'
import { LeafRank } from './LeafRank'

// Function to extract financial metrics based on selected toggles
const extractFinancialMetrics = (
  fund: RecommendedFund,
  selectedToggle: { tenureToggles: string[]; togglelabel: `${ToggleLabel}` },
  selectedTenureToggle: string
): { value: number; isCurrency: boolean } => {
  const { financialMetrics } = fund
  const { togglelabel } = selectedToggle

  // Map toggle labels to metric types
  const metricTypeMap: Record<`${ToggleLabel}`, `${FinancialMetricTypes}`> = {
    [ToggleLabel.ROLLING_RETURNS]: FinancialMetricTypes.ROLLING_RETURNS,
    [ToggleLabel.TRAILING_RETURNS]: FinancialMetricTypes.TRAILING_RETURNS,
    [ToggleLabel.AUM]: FinancialMetricTypes.AUM
  }

  const targetMetricType = metricTypeMap[togglelabel]
  if (!targetMetricType) {
    return { value: 0, isCurrency: false }
  }

  // Find the matching metric
  const matchingMetric = financialMetrics?.find((metric) => {
    if (metric?.metricType !== targetMetricType) return false

    if (targetMetricType === 'AUM') {
      return true // AUM has no tenure
    }

    // For returns metrics, match the tenure
    if (metric?.tenure) {
      const tenureValue = metric?.tenure?.value
      const tenurePeriod = metric?.tenure?.period
      const selectedTenure = selectedTenureToggle?.replace('Y', '')

      return tenureValue === parseFloat(selectedTenure) && tenurePeriod === 'YEAR'
    }

    return false
  })

  if (!matchingMetric) {
    return { value: 0, isCurrency: false }
  }

  const value = matchingMetric?.money?.amount ?? 0
  const isCurrency = targetMetricType === 'AUM'

  return { value, isCurrency }
}

export const KnowWhyExploreFund = ({
  logoSrc,
  title,
  fundType,
  fundCategory,
  formType,
  powerRank,
  isElite,
  selectedToggle,
  selectedTenureToggle,
  fund
}: {
  logoSrc: string
  title: string
  fundType: string
  fundCategory: string
  formType: `${PowerPerformanceForms}`
  powerRank: number
  isElite: boolean
  selectedToggle: {
    tenureToggles: string[]
    togglelabel: `${ToggleLabel}`
  }
  selectedTenureToggle: string
  fund: RecommendedFund
}) => {
  const { value, isCurrency } = extractFinancialMetrics(fund, selectedToggle, selectedTenureToggle)

  const formatValue = (val: number, isCurrency: boolean) => {
    if (isCurrency) {
      return convertNumberToCurrency({ amount: val, isRound: true, isSmall: true })
    } else {
      return typeof val === 'number' && val !== 0 ? `${val?.toFixed(2)}%` : '-'
    }
  }

  return (
    <div className='flex flex-col w-full'>
      <div className='flex flex-col w-full pt-[18px] pb-5 px-4 min-h-[107px]'>
        <div className='flex justify-between'>
          <div className='flex gap-4 w-1 flex-1'>
            <div className='h-10 w-10'>
              <div className='h-10 w-10'>
                <img className='h-full w-full object-contain' src={`${mfLogoBaseUrl}${logoSrc}`} alt='know-why' />
              </div>
            </div>
            <div className='flex flex-col'>
              <div className='text-[13px] text-black w-10/12 leading-[140%]'>{title}</div>
              <div className='pt-[2px] text-[13px] text-[#6A6E6E]'>
                {toPascalCaseWithSpaces(fundType)} / {toPascalCaseWithSpaces(fundCategory)}
              </div>
            </div>
          </div>
          <div>
            <div className='flex flex-col'>
              <div className='text-[13px] text-black font-[500] text-right'>{formatValue(value, isCurrency)}</div>
            </div>
          </div>
        </div>
        <div className='flex items-center gap-2 pt-4 pl-[54px]'>
          <div>
            <LeafRank powerForm={formType} powerRank={powerRank} isElite={isElite} />
          </div>
          <PowerFormBadge powerForm={formType} />
        </div>
      </div>
    </div>
  )
}
